import React, { useCallback, useEffect, useState } from 'react';
import styles from './user-header.module.scss';
import {
  Alert,
  CircleImage,
  DefaultButton,
  formatPhoneNumber,
  ModalWindow,
} from 'shared/ui';
import { MailSvg, PhoneSvg } from 'shared/svg';
import { EditImageComponent } from 'features/index';
import { useAppMediaQuery } from 'shared/hooks';
import { useNavigate } from 'react-router-dom';

interface UserHeaderProps {
  fio: string;
  phone: string;
  email: string;
  imgUrl: string;
  onSaveImage: (url: string) => Promise<{ imgUrl: string } | void>;
  deleteButton?: {
    text: string;
    onClick: () => Promise<void>;
  };
  successMessage?: string;
  extraContent?: React.ReactNode;
}

export const UserHeader: React.FC<UserHeaderProps> = ({
  fio: dataFio,
  phone: dataPhone,
  email: dataEmail,
  imgUrl: dataImgUrl,
  onSaveImage,
  deleteButton,
  successMessage,
  extraContent,
}) => {
  const isTablet = useAppMediaQuery('tablet');

  const [fio, setFio] = useState(dataFio);
  const [phone, setPhone] = useState(dataPhone);
  const [email, setEmail] = useState(dataEmail);
  const [imgUrl, setImgUrl] = useState(dataImgUrl);
  const [showEditImage, setShowEditImage] = useState(false);
  const [modalComponent, setModalComponent] = useState<React.ReactNode>(<></>);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const handleCloseAlert = useCallback(() => setAlertMessage(null), []);
  const navigate = useNavigate();

  useEffect(() => {
    setFio(dataFio);
    setPhone(dataPhone);
    setEmail(dataEmail);
    setImgUrl(dataImgUrl);
  }, [dataFio, dataImgUrl, dataPhone, dataEmail]);

  async function SaveImageUrl(url: string) {
    try {
      const res = await onSaveImage(url);
      if (res && res.imgUrl) {
        setImgUrl(res.imgUrl);
      }
    } catch (error) {
      console.log(error);
    }
  }

  function ChangeImageHandler() {
    setShowEditImage(true);
    setModalComponent(
      <ModalWindow onClickOutside={setShowEditImage}>
        <EditImageComponent setUrl={SaveImageUrl} />
      </ModalWindow>
    );
  }

  async function handleDelete() {
    if (deleteButton) {
      try {
        await deleteButton.onClick();
        if (successMessage) {
          setAlertMessage(successMessage);
          setTimeout(() => navigate(-1), 1500);
        } else {
          navigate(-1);
        }
      } catch (error) {
        console.log(error);
      }
    }
  }

  return (
    <div className={styles.user_header}>
      {showEditImage ? modalComponent : null}

      <div className={styles.user_header__content}>
        <div className={styles.content__user_info}>
          <div className={styles.user_info__data}>
            <div className={styles.data__image}>
              <CircleImage
                imgSize={isTablet ? 70 : 90}
                imgSrc={imgUrl}
                onClick={ChangeImageHandler}
              />
            </div>
            <div className={styles.data__info}>
              <div className={styles.info__full_name}>{fio}</div>
              <div className={styles.info__phone}>
                <PhoneSvg></PhoneSvg>
                {formatPhoneNumber(phone)}
              </div>
              <div className={styles.info__mail}>
                <MailSvg />
                {email}
              </div>
            </div>
          </div>
          <div className={styles.user_info__buttons}>
            {deleteButton ? (
              <DefaultButton
                buttonText={deleteButton.text}
                color="red"
                className={styles.button_delete}
                onClick={handleDelete}
              ></DefaultButton>
            ) : null}
          </div>
        </div>
        {extraContent ? (
          <div className={styles.content__work_schedule}>{extraContent}</div>
        ) : null}
      </div>
      {alertMessage && (
        <Alert
          type="success"
          message={alertMessage}
          onClose={handleCloseAlert}
        />
      )}
    </div>
  );
};
